// Shared Shelton-style page primitives. Every content page uses these to
// stay visually consistent: overline (tick + kicker), big serif H1 in the
// heading color, and numbered content rows with a subtle divider.

export function PageHero({ kicker, title, intro, testId }) {
    return (
        <section className="bg-acre-bg" data-testid={testId}>
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 pt-16 lg:pt-24 pb-10">
                <div className="flex items-center mb-6">
                    <span className="acre-tick" />
                    <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">
                        {kicker}
                    </span>
                </div>
                <h1 className="font-serif-display text-5xl lg:text-7xl leading-[0.98] tracking-tight text-acre-secondary max-w-[1100px]">
                    {title}
                </h1>
                {intro && (
                    <p className="mt-7 max-w-2xl text-base lg:text-lg leading-relaxed text-acre-muted">
                        {intro}
                    </p>
                )}
            </div>
        </section>
    );
}

export function SectionHeader({ kicker, title, description }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-20">
            <div className="lg:col-span-5">
                {kicker && (
                    <div className="flex items-center mb-6">
                        <span className="acre-tick" />
                        <span className="text-[11px] uppercase tracking-[0.28em] text-acre-muted">{kicker}</span>
                    </div>
                )}
                <h2 className="font-serif-display text-4xl lg:text-6xl tracking-tight leading-[1.02] text-acre-secondary">
                    {title}
                </h2>
            </div>
            {description && (
                <div className="lg:col-span-7 flex items-end">
                    <p className="text-acre-muted text-base lg:text-lg leading-[1.7] max-w-2xl">
                        {description}
                    </p>
                </div>
            )}
        </div>
    );
}

// Shelton-style numbered row. `href` is optional — if provided, the whole row
// becomes a link. Each row: 01 · Title · Description · optional arrow.
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export function NumberedList({ items }) {
    return (
        <ul className="divide-y divide-acre-line border-y border-acre-line">
            {items.map((it, idx) => {
                const n = String(idx + 1).padStart(2, "0");
                const Row = (
                    <div className="group grid grid-cols-12 gap-4 lg:gap-8 py-8 lg:py-10 items-start transition-colors hover:bg-[#FBFAF8]">
                        <div className="col-span-2 lg:col-span-1 font-serif-display text-2xl lg:text-3xl leading-none pt-1">
                            {n}
                        </div>
                        <div className="col-span-10 lg:col-span-4">
                            <h3 className="font-serif-display text-3xl lg:text-4xl text-acre-ink leading-tight group-hover:text-acre-secondary transition-colors">
                                {it.title}
                            </h3>
                        </div>
                        <div className="col-span-12 lg:col-span-6 mt-2 lg:mt-0">
                            <div className="text-acre-muted text-base leading-relaxed max-w-xl">
                                {it.body}
                            </div>
                        </div>
                        <div className="col-span-12 lg:col-span-1 flex lg:justify-end mt-2 lg:mt-0">
                            {it.href && (
                                <ArrowUpRight
                                    size={22}
                                    strokeWidth={1.5}
                                    className="text-acre-ink group-hover:text-acre-secondary group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                                />
                            )}
                        </div>
                    </div>
                );
                return (
                    <li key={idx} data-testid={it.testId}>
                        {it.href ? (
                            <Link to={it.href} className="block">{Row}</Link>
                        ) : Row}
                    </li>
                );
            })}
        </ul>
    );
}
